import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import React, { useState } from 'react'

function GenericTable({ columns, data, pagination, onPageChange, isLoading }) {
  const [hoveredRow, setHoveredRow] = useState(null)

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  })

  // Corrected calculations using pagination.total and pagination.limit
  const startItem =
    pagination?.total > 0 && pagination?.page && pagination?.limit
      ? (pagination.page - 1) * pagination.limit + 1
      : 0
  const endItem =
    pagination?.page && pagination?.limit && pagination?.total
      ? Math.min(pagination.page * pagination.limit, pagination.total)
      : 0

  return (
    <div className="overflow-hidden rounded-xl border border-purple-100 bg-white shadow-lg transition-all duration-300 hover:shadow-xl">
      {/* Elegant Header Bar */}
      {/* <div className="bg-gradient-to-r from-[#F7E5FF] via-[#F7E6FF] to-[#D6E0FF] w-full h-20 flex items-center px-6 rounded-t-xl">
        <h3 className="text-xl font-semibold text-theme-color-800 drop-shadow-sm flex items-center gap-2">
          ✨ Data Collection
        </h3>
      </div> */}

      {/* Table Container */}
      <div className="overflow-x-auto "
      style={{
        scrollbarWidth:"none"
      }}>
        <table className="min-w-full divide-y divide-purple-100">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="bg-purple-50 px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-theme-color-800"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-purple-50 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <svg
                      className="h-10 w-10 animate-spin text-theme-color-800"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="mt-3 text-sm font-medium text-theme-color-800">
                      Preparing your data...
                    </span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <svg
                      className="h-16 w-16 text-purple-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      ></path>
                    </svg>
                    <p className="mt-4 text-lg font-medium text-theme-color-800">No data available</p>
                    <p className="mt-1 text-sm text-theme-color-800">
                      Try adjusting your filters or search criteria
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`
                        transition-all duration-200
                        ${
                          hoveredRow === row.id
                            ? 'bg-purple-50 transform scale-[1.01]'
                            : index % 2 === 0
                              ? 'bg-white'
                              : 'bg-purple-50/30'
                        }
                      `}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-700"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination && (
  <div className="border-t border-purple-100 bg-white px-4 py-3 sm:px-6 sm:py-4">
    <div className="flex flex-col items-center justify-between gap-3 sm:flex-row sm:gap-4">
      {/* Results count - always visible */}
      {/* <div className="text-center sm:text-left">
        <span className="text-sm font-medium text-theme-color-800">
          Showing <span className="font-bold text-purple-900">{startItem}</span> to{' '}
          <span className="font-bold text-purple-900">{endItem}</span> of{' '}
          <span className="font-bold text-purple-900">{pagination.total}</span> results
        </span>
      </div> */}

      {/* Pagination controls - responsive layout */}
      <div className="flex flex-col justify-between items-center gap-3 sm:flex-row sm:gap-4 hidden space-x-2 sm:flex ml-auto">
        {/* Mobile: Compact controls */}
        <div className="flex items-center sm:hidden">
          <button
            className="inline-flex items-center rounded-lg border border-purple-200 bg-white p-2 text-sm font-medium text-theme-color-800 shadow-sm transition-all hover:bg-purple-50 hover:text-theme-color-800 hover:shadow disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-theme-color-800"
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1 || isLoading}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          
          <div className="mx-2 flex items-center rounded-lg border border-purple-200 bg-purple-50 px-3 py-1">
            <span className="text-sm font-bold text-theme-color-800">{pagination.page}</span>
            <span className="mx-1 text-sm text-theme-color-800">/</span>
            <span className="text-sm text-theme-color-800">{pagination.totalPages}</span>
          </div>
          
          <button
            className="inline-flex items-center rounded-lg border border-purple-200 bg-white p-2 text-sm font-medium text-theme-color-800 shadow-sm transition-all hover:bg-purple-50 hover:text-theme-color-800 hover:shadow disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-theme-color-800"
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages || isLoading}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Desktop: Full controls */}
        <div className="hidden space-x-2 sm:flex">
          {/* First */}
          <button
              className="inline-flex items-center rounded-lg border border-purple-200 bg-white px-3 py-2 text-theme-color-800 shadow-sm hover:bg-purple-50 hover:text-theme-color-800 disabled:opacity-50 disabled:cursor-not-allowed text-2xl"
            onClick={() => onPageChange(1)}
            disabled={pagination.page <= 1 || isLoading}
          >
            «
          </button>

          {/* Prev */}
          <button
            className="inline-flex items-center rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm font-medium text-theme-color-800 shadow-sm transition-all hover:bg-purple-50 hover:text-theme-color-800 hover:shadow disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-theme-color-800"
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1 || isLoading}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Current Page */}
          <div className="flex items-center rounded-lg border border-purple-200 bg-purple-50 px-3 py-2">
            <span className="text-sm font-bold text-theme-color-800">{pagination.page}</span>
            <span className="mx-1 text-sm text-theme-color-800">/</span>
            <span className="text-sm text-theme-color-800">{pagination.totalPages}</span>
          </div>

          {/* Next */}
          <button
            className="inline-flex items-center rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm font-medium text-theme-color-800 shadow-sm transition-all hover:bg-purple-50 hover:text-theme-color-800 hover:shadow disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-theme-color-800"
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages || isLoading}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Last */}
          <button
            className="inline-flex items-center rounded-lg border border-purple-200 bg-white px-3 py-2 text-theme-color-800 shadow-sm hover:bg-purple-50 hover:text-theme-color-800 disabled:opacity-50 disabled:cursor-not-allowed text-2xl"
            onClick={() => onPageChange(pagination.totalPages)}
            disabled={pagination.page >= pagination.totalPages || isLoading}
          >
            »
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  )
}

export default GenericTable
